<template>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div>
          <span class="admin-kicker">Datenverwaltung</span>
          <h1 class="admin-page-title">Tarife</h1>
        </div>
      </div>

      <div>
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="text-center">
            <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-caspyellow mb-4"></div>
            <p class="text-gray-600 font-medium">Lade Tarife...</p>
          </div>
        </div>
        
        <div v-else class="space-y-6">
          <div class="admin-tabs">
            <button
              type="button"
              class="admin-tab"
              :class="{ 'admin-tab--active': activeTab === 'basistarife' }"
              @click="activeTab = 'basistarife'"
            >
              Basistarife
            </button>
            <button
              type="button"
              class="admin-tab"
              :class="{ 'admin-tab--active': activeTab === 'verrechnungstarife' }"
              @click="activeTab = 'verrechnungstarife'"
            >
              Verrechnungstarife
            </button>
            <button
              type="button"
              class="admin-tab"
              :class="{ 'admin-tab--active': activeTab === 'subtarife' }"
              @click="activeTab = 'subtarife'"
            >
              Sub-Tarife
            </button>
          </div>

          <div v-if="activeTab === 'basistarife'" class="space-y-6">
            <div class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
              <div class="px-6 py-4" style="background-color: #5f656e;">
                <h3 class="text-2xl font-bold text-white">Neuer Basistarif</h3>
              </div>
              <div class="p-6 space-y-6">
                <div class="flex gap-4">
                  <input
                    v-model="newBasistarif.name"
                    @keyup.enter="createBasistarif"
                    class="flex-1 border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    placeholder="Name des Basistarifs"
                  />
                  <button
                    type="button"
                    @click="createBasistarif"
                    :disabled="!newBasistarif.name.trim()"
                    class="admin-button--primary"
                  >
                    Erstellen
                  </button>
                </div>
                
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Laufzeit Von</label>
                    <input
                      v-model="newBasistarif.laufzeitVon"
                      type="date"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-semibold text-gray-700 mb-2">Laufzeit Bis</label>
                    <input
                      v-model="newBasistarif.laufzeitBis"
                      type="date"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    />
                  </div>
                </div>
                
                <div class="space-y-3">
                  <label class="block text-sm font-semibold text-gray-700">Kategorie-Preise</label>
                  <div class="border-2 border-gray-300 rounded-xl p-4 bg-gray-50 max-h-96 overflow-y-auto">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div v-for="category in categories" :key="category" class="space-y-1">
                        <label class="block text-xs font-medium text-gray-600">{{ category }}</label>
                        <div class="relative">
                          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{{ isPercentageCategory(category) ? '%' : '€' }}</span>
                          <input
                            v-model.number="newBasistarif.categoryPrices[category]"
                            type="number"
                            step="0.01"
                            min="0"
                            class="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-caspyellow focus:ring-1 focus:ring-caspyellow focus:outline-none transition-all duration-200"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-for="tarif in basistarife" :key="tarif.id" class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200">
              <div class="p-6">
                <div v-if="editingBasistarif === tarif.id" class="space-y-6">
                  <div class="flex gap-4 items-center">
                    <input
                      v-model="editBasistarifData.name"
                      @keyup.enter="saveBasistarif(tarif.id)"
                      @keyup.esc="cancelEditBasistarif"
                      class="flex-1 border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                      placeholder="Name"
                    />
                  </div>
                  
                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-semibold text-gray-700 mb-2">Laufzeit Von</label>
                      <input
                        v-model="editBasistarifData.laufzeitVon"
                        type="date"
                        class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label class="block text-sm font-semibold text-gray-700 mb-2">Laufzeit Bis</label>
                      <input
                        v-model="editBasistarifData.laufzeitBis"
                        type="date"
                        class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                      />
                    </div>
                  </div>
                  
                  <div class="space-y-3">
                    <label class="block text-sm font-semibold text-gray-700">Kategorie-Preise</label>
                    <div class="border-2 border-gray-300 rounded-xl p-4 bg-gray-50 max-h-96 overflow-y-auto">
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div v-for="category in categories" :key="category" class="space-y-1">
                          <label class="block text-xs font-medium text-gray-600">{{ category }}</label>
                          <div class="relative">
                            <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{{ isPercentageCategory(category) ? '%' : '€' }}</span>
                            <input
                              v-model.number="editBasistarifData.categoryPrices[category]"
                              type="number"
                              step="0.01"
                              min="0"
                              class="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-caspyellow focus:ring-1 focus:ring-caspyellow focus:outline-none transition-all duration-200"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="flex gap-3 justify-end">
                    <button
                      type="button"
                      @click="cancelEditBasistarif"
                      class="admin-button--secondary"
                    >
                      Abbrechen
                    </button>
                    <button
                      type="button"
                      @click="saveBasistarif(tarif.id)"
                      class="admin-button--primary"
                    >
                      Speichern
                    </button>
                  </div>
                </div>
                <div v-else class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div class="flex-1 space-y-3">
                    <div>
                      <h3 class="text-2xl font-bold text-gray-800">{{ tarif.name }}</h3>
                      <p class="text-sm text-gray-500 mt-1">ID: {{ tarif.id }}</p>
                      <div v-if="tarif.laufzeitVon && tarif.laufzeitBis" class="flex flex-wrap gap-2 mt-2">
                        <span class="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-blue-100 text-blue-800">
                          {{ formatDate(tarif.laufzeitVon) }} - {{ formatDate(tarif.laufzeitBis) }}
                        </span>
                      </div>
                    </div>
                    <div v-if="tarif.categoryPrices && Object.keys(tarif.categoryPrices).length > 0" class="space-y-2">
                      <p class="text-sm font-semibold text-gray-700">Kategorie-Preise:</p>
                      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                        <div v-for="(price, category) in tarif.categoryPrices" :key="category" v-show="price" class="bg-green-50 border border-green-200 rounded px-2 py-1">
                          <span class="font-medium text-gray-700">{{ category }}:</span>
                          <span class="text-green-700 font-semibold ml-1">{{ isPercentageCategory(category) ? parseFloat(price).toFixed(2) + '%' : '€' + parseFloat(price).toFixed(2) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex gap-3">
                    <button
                      type="button"
                      @click="startEditBasistarif(tarif)"
                      class="admin-button--secondary"
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      @click="deleteBasistarif(tarif.id)"
                      class="admin-button--danger"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="basistarife.length === 0" class="text-center py-16">
              <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-200 mb-4">
                <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <p class="text-gray-500 text-lg font-medium">Keine Basistarife vorhanden</p>
            </div>
          </div>

          <div v-if="activeTab === 'verrechnungstarife'" class="space-y-6">
            <div class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
              <div class="px-6 py-4" style="background-color: #5f656e;">
                <h3 class="text-2xl font-bold text-white">Neuer Verrechnungstarif</h3>
              </div>
              <div class="p-6 space-y-6">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-semibold text-gray-700">Name</label>
                    <input
                      v-model="newVerrechnungstarif.name"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                      placeholder="Name des Verrechnungstarifs"
                    />
                  </div>
                  <div class="space-y-2">
                    <label class="block text-sm font-semibold text-gray-700">Laufzeit Von</label>
                    <input
                      v-model="newVerrechnungstarif.laufzeitVon"
                      type="date"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    />
                  </div>
                  <div class="space-y-2">
                    <label class="block text-sm font-semibold text-gray-700">Laufzeit Bis</label>
                    <input
                      v-model="newVerrechnungstarif.laufzeitBis"
                      type="date"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    />
                  </div>
                </div>
                
                <div class="space-y-3">
                  <label class="block text-sm font-semibold text-gray-700">Kategorie-Preise</label>
                  <div class="border-2 border-gray-300 rounded-xl p-4 bg-gray-50 max-h-96 overflow-y-auto">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div v-for="category in categories" :key="category" class="space-y-1">
                        <label class="block text-xs font-medium text-gray-600">{{ category }}</label>
                        <div class="relative">
                          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{{ isPercentageCategory(category) ? '%' : '€' }}</span>
                          <input
                            v-model.number="newVerrechnungstarif.categoryPrices[category]"
                            type="number"
                            step="0.01"
                            min="0"
                            class="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-caspyellow focus:ring-1 focus:ring-caspyellow focus:outline-none transition-all duration-200"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="flex justify-end pt-2">
                  <button
                    type="button"
                    @click="createVerrechnungstarif"
                    :disabled="!isNewVerrechnungstarifValid"
                    class="admin-button--primary"
                  >
                    Verrechnungstarif erstellen
                  </button>
                </div>
              </div>
            </div>

            <div v-for="tarif in groupedVerrechnungstarife" :key="tarif.vertragsbeschreibung" class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200">
              <div class="px-6 py-4" style="background-color: #5f656e;">
                <h3 class="text-xl font-bold text-white">{{ tarif.vertragsbeschreibung }}</h3>
              </div>
              <div class="divide-y divide-gray-200">
                <div v-for="vt in tarif.tarife" :key="vt.id" class="p-6">
                  <div v-if="editingVerrechnungstarif === vt.id" class="space-y-6">
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div class="space-y-2">
                        <label class="block text-sm font-semibold text-gray-700">Name</label>
                        <input
                          v-model="editVerrechnungstarifData.name"
                          class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                        />
                      </div>
                      <div class="space-y-2">
                        <label class="block text-sm font-semibold text-gray-700">Vertragsbeschreibung</label>
                        <select
                          v-model="editVerrechnungstarifData.vertragsbeschreibung"
                          class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                        >
                          <option v-for="vb in vertragsbeschreibungen" :key="vb" :value="vb">{{ vb }}</option>
                        </select>
                      </div>
                      <div class="space-y-2">
                        <label class="block text-sm font-semibold text-gray-700">Laufzeit Von</label>
                        <input
                          v-model="editVerrechnungstarifData.laufzeitVon"
                          type="date"
                          class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                        />
                      </div>
                      <div class="space-y-2">
                        <label class="block text-sm font-semibold text-gray-700">Laufzeit Bis</label>
                        <input
                          v-model="editVerrechnungstarifData.laufzeitBis"
                          type="date"
                          class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                        />
                      </div>
                    </div>
                    
                    <div class="space-y-3">
                      <label class="block text-sm font-semibold text-gray-700">Kategorie-Preise</label>
                      <div class="border-2 border-gray-300 rounded-xl p-4 bg-gray-50 max-h-96 overflow-y-auto">
                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div v-for="category in categories" :key="category" class="space-y-1">
                            <label class="block text-xs font-medium text-gray-600">{{ category }}</label>
                            <div class="relative">
                              <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{{ isPercentageCategory(category) ? '%' : '€' }}</span>
                              <input
                                v-model.number="editVerrechnungstarifData.categoryPrices[category]"
                                type="number"
                                step="0.01"
                                min="0"
                                class="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-caspyellow focus:ring-1 focus:ring-caspyellow focus:outline-none transition-all duration-200"
                                placeholder="0.00"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div class="flex gap-3 justify-end pt-2">
                      <button
                        type="button"
                        @click="cancelEditVerrechnungstarif"
                        class="admin-button--secondary"
                      >
                        Abbrechen
                      </button>
                      <button
                        type="button"
                        @click="saveVerrechnungstarif(vt.id)"
                        class="admin-button--primary"
                      >
                        Speichern
                      </button>
                    </div>
                  </div>
                  <div v-else>
                    <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div class="flex-1 space-y-3">
                        <h4 class="text-xl font-bold text-gray-800">{{ vt.name }}</h4>
                        <div class="flex flex-wrap gap-2">
                          <span class="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-gray-100 text-gray-800">
                            {{ formatDate(vt.laufzeitVon) }} - {{ formatDate(vt.laufzeitBis) }}
                          </span>
                        </div>
                        <div v-if="vt.categoryPrices && Object.keys(vt.categoryPrices).length > 0" class="space-y-2">
                          <p class="text-sm font-semibold text-gray-700">Kategorie-Preise:</p>
                          <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
                            <div v-for="(price, category) in vt.categoryPrices" :key="category" v-show="price" class="bg-green-50 border border-green-200 rounded px-2 py-1">
                              <span class="font-medium text-gray-700">{{ category }}:</span>
                              <span class="text-green-700 font-semibold ml-1">{{ isPercentageCategory(category) ? parseFloat(price).toFixed(2) + '%' : '€' + parseFloat(price).toFixed(2) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="flex flex-wrap gap-2">
                        <button
                          type="button"
                          @click="copyVerrechnungstarif(vt)"
                          class="admin-button--secondary"
                        >
                          Kopieren
                        </button>
                        <button
                          type="button"
                          @click="startEditVerrechnungstarif(vt)"
                          class="admin-button--secondary"
                        >
                          Bearbeiten
                        </button>
                        <button
                          type="button"
                          @click="deleteVerrechnungstarif(vt.id)"
                          class="admin-button--danger"
                        >
                          Löschen
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="verrechnungstarife.length === 0" class="text-center py-16">
              <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-200 mb-4">
                <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <p class="text-gray-500 text-lg font-medium">Keine Verrechnungstarife vorhanden</p>
            </div>
          </div>

          <div v-if="activeTab === 'subtarife'" class="space-y-6">
            <div class="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
              <div class="px-6 py-4" style="background-color: #5f656e;">
                <h3 class="text-2xl font-bold text-white">Neuer Sub-Tarif</h3>
              </div>
              <div class="p-6 space-y-6">
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-700">Aus Vorlage übernehmen (optional)</label>
                  <select v-model="subtarifTemplateKey" @change="applySubtarifTemplate"
                    class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none">
                    <option value="">Keine Vorlage</option>
                    <optgroup label="Verrechnungstarife">
                      <option v-for="vt in verrechnungstarife" :key="'vt-' + vt.id" :value="'vt-' + vt.id">
                        {{ vt.name }} ({{ formatDate(vt.laufzeitVon) }} - {{ formatDate(vt.laufzeitBis) }})
                      </option>
                    </optgroup>
                    <optgroup label="Sub-Tarife">
                      <option v-for="st in subtarife" :key="'st-' + st.id" :value="'st-' + st.id">
                        {{ st.name }} ({{ formatDate(st.laufzeitVon) }} - {{ formatDate(st.laufzeitBis) }})
                      </option>
                    </optgroup>
                  </select>
                </div>
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div class="space-y-2">
                    <label class="block text-sm font-semibold text-gray-700">Name</label>
                    <input v-model="newSubtarif.name"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none"
                      placeholder="Name des Sub-Tarifs" />
                  </div>
                  <div class="space-y-2">
                    <label class="block text-sm font-semibold text-gray-700">Laufzeit Von</label>
                    <input v-model="newSubtarif.laufzeitVon" type="date"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none" />
                  </div>
                  <div class="space-y-2">
                    <label class="block text-sm font-semibold text-gray-700">Laufzeit Bis</label>
                    <input v-model="newSubtarif.laufzeitBis" type="date"
                      class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none" />
                  </div>
                </div>
                <div class="space-y-3">
                  <label class="block text-sm font-semibold text-gray-700">Kategorie-Preise (Eingangspreise)</label>
                  <div class="border-2 border-gray-300 rounded-xl p-4 bg-gray-50 max-h-96 overflow-y-auto">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div v-for="category in categories" :key="category" class="space-y-1">
                        <label class="block text-xs font-medium text-gray-600">{{ category }}</label>
                        <div class="relative">
                          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{{ isPercentageCategory(category) ? '%' : '€' }}</span>
                          <input v-model.number="newSubtarif.categoryPrices[category]" type="number" step="0.01" min="0"
                            class="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:border-caspyellow focus:outline-none"
                            placeholder="0.00" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flex justify-end">
                  <button type="button" @click="createSubtarif" :disabled="!isSubtarifValid"
                    class="admin-button--primary">Sub-Tarif erstellen</button>
                </div>
              </div>
            </div>

            <div v-for="tarif in subtarife" :key="tarif.id"
              class="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">
              <div class="p-6">
                <div v-if="editingSubtarif === tarif.id" class="space-y-6">
                  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div class="space-y-2">
                      <label class="block text-sm font-semibold text-gray-700">Name</label>
                      <input v-model="editSubtarifData.name"
                        class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none" />
                    </div>
                    <div class="space-y-2">
                      <label class="block text-sm font-semibold text-gray-700">Laufzeit Von</label>
                      <input v-model="editSubtarifData.laufzeitVon" type="date"
                        class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none" />
                    </div>
                    <div class="space-y-2">
                      <label class="block text-sm font-semibold text-gray-700">Laufzeit Bis</label>
                      <input v-model="editSubtarifData.laufzeitBis" type="date"
                        class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:outline-none" />
                    </div>
                  </div>
                  <div class="space-y-3">
                    <label class="block text-sm font-semibold text-gray-700">Kategorie-Preise (Eingangspreise)</label>
                    <div class="border-2 border-gray-300 rounded-xl p-4 bg-gray-50 max-h-96 overflow-y-auto">
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div v-for="category in categories" :key="category" class="space-y-1">
                          <label class="block text-xs font-medium text-gray-600">{{ category }}</label>
                          <input v-model.number="editSubtarifData.categoryPrices[category]" type="number" step="0.01" min="0"
                            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-caspyellow focus:outline-none"
                            placeholder="0.00" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex gap-3 justify-end">
                    <button type="button" @click="cancelEditSubtarif" class="admin-button--secondary">Abbrechen</button>
                    <button type="button" @click="saveSubtarif(tarif.id)" class="admin-button--primary">Speichern</button>
                  </div>
                </div>
                <div v-else class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div class="flex-1 space-y-3">
                    <div>
                      <h3 class="text-2xl font-bold text-gray-800">{{ tarif.name }}</h3>
                      <p class="text-sm text-gray-500 mt-1">ID: {{ tarif.id }}</p>
                      <span class="inline-flex items-center px-3 py-1 mt-2 rounded-lg text-sm font-medium bg-blue-100 text-blue-800">
                        {{ formatDate(tarif.laufzeitVon) }} - {{ formatDate(tarif.laufzeitBis) }}
                      </span>
                    </div>
                    <div v-if="tarif.categoryPrices && Object.keys(tarif.categoryPrices).length" class="space-y-2">
                      <p class="text-sm font-semibold text-gray-700">Eingangspreise:</p>
                      <div class="flex flex-wrap gap-2 text-xs">
                        <span v-for="(price, category) in tarif.categoryPrices" :key="category" v-show="price"
                          class="bg-green-50 border border-green-200 rounded px-2 py-1">
                          <span class="font-medium text-gray-700">{{ category }}:</span>
                          <span class="text-green-700 font-semibold ml-1">{{ isPercentageCategory(category) ? parseFloat(price).toFixed(2) + '%' : '€' + parseFloat(price).toFixed(2) }}</span>
                        </span>
                      </div>
                    </div>
                    <div class="text-sm text-gray-600">
                      <span class="font-semibold">Zugeordnet:</span>
                      <span v-if="tarif.kontakte && tarif.kontakte.length">
                        {{ tarif.kontakte.map(kontakt => kontakt.unternehmensname).join(', ') }}
                      </span>
                      <span v-else>keine Subunternehmer</span>
                    </div>
                  </div>
                  <div class="flex flex-wrap gap-3">
                    <button type="button" @click="copySubtarif(tarif)" class="admin-button--secondary">Kopieren</button>
                    <button type="button" @click="startEditSubtarif(tarif)" class="admin-button--secondary">Bearbeiten</button>
                    <button type="button" @click="deleteSubtarif(tarif.id)" class="admin-button--danger">Löschen</button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="subtarife.length === 0" class="text-center py-16">
              <p class="text-gray-500 text-lg font-medium">Keine Sub-Tarife vorhanden</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CASP from '../../CASP';

export default {
  data() {
    return {
      loading: true,
      activeTab: 'basistarife',
      basistarife: [],
      verrechnungstarife: [],
      subtarife: [],
      vertragsbeschreibungen: [],
      categories: [
        'ZEITKONTO',
        'CASP OFFICE',
        'OBJEKTSCHUTZ A-1A',
        'OBJEKTSCHUTZ A-1B',
        'OBJEKTSCHUTZ A-2BA',
        'OBJEKTSCHUTZ A-2BB',
        'OBJEKTSCHUTZ A-2C',
        'OBJEKTSCHUTZ A-3BB',
        'OBJEKTSCHUTZ A-7B',
        'OBJEKTSCHUTZ B-11A',
        'DETEKTIV',
        'VA SCHUTZ',
        'VA BEWACHUNG',
        'SUPERVISOR',
        'EINSATZLEITER',
        'OBJEKTLEITER',
        'ZL NACHT',
        'ZL SONNTAG',
        'ZL FEIERTAG',
        'KRANK',
        'URLAUB'
      ],
      percentageCategories: ['ZL NACHT', 'ZL SONNTAG', 'ZL FEIERTAG'],
      newBasistarif: {
        name: '',
        laufzeitVon: '',
        laufzeitBis: '',
        categoryPrices: {}
      },
      newVerrechnungstarif: {
        name: '',
        laufzeitVon: '',
        laufzeitBis: '',
        categoryPrices: {}
      },
      newSubtarif: {
        name: '',
        laufzeitVon: '',
        laufzeitBis: '',
        categoryPrices: {}
      },
      subtarifTemplateKey: '',
      editingBasistarif: null,
      editBasistarifData: {
        name: '',
        laufzeitVon: '',
        laufzeitBis: '',
        categoryPrices: {}
      },
      editingVerrechnungstarif: null,
      editVerrechnungstarifData: {
        name: '',
        vertragsbeschreibung: '',
        laufzeitVon: '',
        laufzeitBis: '',
        categoryPrices: {}
      },
      editingSubtarif: null,
      editSubtarifData: {
        name: '',
        laufzeitVon: '',
        laufzeitBis: '',
        categoryPrices: {}
      }
    };
  },
  computed: {
    isNewVerrechnungstarifValid() {
      return this.newVerrechnungstarif.name.trim() &&
             this.newVerrechnungstarif.laufzeitVon &&
             this.newVerrechnungstarif.laufzeitBis;
    },
    isSubtarifValid() {
      return this.newSubtarif.name.trim() &&
             this.newSubtarif.laufzeitVon &&
             this.newSubtarif.laufzeitBis;
    },
    groupedVerrechnungstarife() {
      const grouped = {};
      this.verrechnungstarife.forEach(tarif => {
        if (!grouped[tarif.vertragsbeschreibung]) {
          grouped[tarif.vertragsbeschreibung] = {
            vertragsbeschreibung: tarif.vertragsbeschreibung,
            tarife: []
          };
        }
        grouped[tarif.vertragsbeschreibung].tarife.push(tarif);
      });
      return Object.values(grouped);
    }
  },
  methods: {
    isPercentageCategory(category) {
      return this.percentageCategories.includes(category);
    },
    loadData() {
      this.loading = true;
      let tarifeLoaded = false;
      let vertragsbeschreibungenLoaded = false;
      
      CASP.getTarife((status, response) => {
        if (status === 200) {
          this.basistarife = response.basistarife.map(tarif => ({
            ...tarif,
            categoryPrices: tarif.categoryPrices || {}
          }));
          this.verrechnungstarife = response.verrechnungstarife.map(tarif => ({
            ...tarif,
            categoryPrices: tarif.categoryPrices || {}
          }));
          this.subtarife = (response.subtarife || []).map(tarif => ({
            ...tarif,
            categoryPrices: tarif.categoryPrices || {}
          }));
          tarifeLoaded = true;
          if (vertragsbeschreibungenLoaded) this.loading = false;
        } else {
          console.error('Fehler beim Laden der Tarife:', response);
          alert('Fehler beim Laden der Tarife: ' + (response?.error || response));
          this.loading = false;
        }
      });
      
      CASP.getVertragsbeschreibungen((status, response) => {
        if (status === 200) {
          this.vertragsbeschreibungen = response;
          vertragsbeschreibungenLoaded = true;
          if (tarifeLoaded) this.loading = false;
        } else {
          console.error('Fehler beim Laden der Vertragsbeschreibungen:', response);
          alert('Fehler beim Laden der Vertragsbeschreibungen: ' + (response?.error || response));
          this.loading = false;
        }
      });
    },
    createBasistarif() {
      if (!this.newBasistarif.name.trim()) return;
      CASP.createBasistarif(this.newBasistarif, (status, response) => {
        if (status === 200) {
          this.newBasistarif = {
            name: '',
            laufzeitVon: '',
            laufzeitBis: '',
            categoryPrices: {}
          };
          this.loadData();
        } else {
          console.error('Fehler beim Erstellen des Basistarifs:', response);
          alert('Fehler beim Erstellen des Basistarifs: ' + (response?.error || response));
        }
      });
    },
    startEditBasistarif(tarif) {
      this.editingBasistarif = tarif.id;
      this.editBasistarifData = {
        name: tarif.name,
        laufzeitVon: tarif.laufzeitVon || '',
        laufzeitBis: tarif.laufzeitBis || '',
        categoryPrices: tarif.categoryPrices ? { ...tarif.categoryPrices } : {}
      };
    },
    saveBasistarif(id) {
      CASP.updateBasistarif(id, this.editBasistarifData, (status, response) => {
        if (status === 200) {
          this.editingBasistarif = null;
          this.loadData();
        } else {
          console.error('Fehler beim Speichern des Basistarifs:', response);
          alert('Fehler beim Speichern des Basistarifs: ' + (response?.error || response));
        }
      });
    },
    cancelEditBasistarif() {
      this.editingBasistarif = null;
      this.editBasistarifData = {
        name: '',
        categoryPrices: {}
      };
    },
    deleteBasistarif(id) {
      if (!confirm('Möchten Sie diesen Basistarif wirklich löschen?')) return;
      CASP.deleteBasistarif(id, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          console.error('Fehler beim Löschen des Basistarifs:', response);
          alert('Fehler beim Löschen des Basistarifs: ' + (response?.error || response));
        }
      });
    },
    createVerrechnungstarif() {
      if (!this.isNewVerrechnungstarifValid) return;
      CASP.createVerrechnungstarif(this.newVerrechnungstarif, (status, response) => {
        if (status === 200) {
          this.newVerrechnungstarif = {
            name: '',
            laufzeitVon: '',
            laufzeitBis: '',
            categoryPrices: {}
          };
          this.loadData();
        } else {
          console.error('Fehler beim Erstellen des Verrechnungstarifs:', response);
          alert('Fehler beim Erstellen des Verrechnungstarifs: ' + (response?.error || response));
        }
      });
    },
    startEditVerrechnungstarif(tarif) {
      this.editingVerrechnungstarif = tarif.id;
      this.editVerrechnungstarifData = {
        name: tarif.name,
        vertragsbeschreibung: tarif.vertragsbeschreibung,
        laufzeitVon: tarif.laufzeitVon,
        laufzeitBis: tarif.laufzeitBis,
        categoryPrices: tarif.categoryPrices ? { ...tarif.categoryPrices } : {}
      };
    },
    saveVerrechnungstarif(id) {
      CASP.updateVerrechnungstarif(id, this.editVerrechnungstarifData, (status, response) => {
        if (status === 200) {
          this.editingVerrechnungstarif = null;
          this.loadData();
        } else {
          console.error('Fehler beim Speichern des Verrechnungstarifs:', response);
          alert('Fehler beim Speichern des Verrechnungstarifs: ' + (response?.error || response));
        }
      });
    },
    cancelEditVerrechnungstarif() {
      this.editingVerrechnungstarif = null;
      this.editVerrechnungstarifData = {
        name: '',
        vertragsbeschreibung: '',
        laufzeitVon: '',
        laufzeitBis: '',
        categoryPrices: {}
      };
    },
    deleteVerrechnungstarif(id) {
      if (!confirm('Möchten Sie diesen Verrechnungstarif wirklich löschen?')) return;
      CASP.deleteVerrechnungstarif(id, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          console.error('Fehler beim Löschen des Verrechnungstarifs:', response);
          alert('Fehler beim Löschen des Verrechnungstarifs: ' + (response?.error || response));
        }
      });
    },
    copyVerrechnungstarif(tarif) {
      const laufzeitVon = prompt('Laufzeit Von (YYYY-MM-DD):', tarif.laufzeitVon);
      if (!laufzeitVon) return;
      const laufzeitBis = prompt('Laufzeit Bis (YYYY-MM-DD):', tarif.laufzeitBis);
      if (!laufzeitBis) return;
      
      CASP.copyVerrechnungstarif(tarif.id, { laufzeitVon, laufzeitBis }, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          console.error('Fehler beim Kopieren des Verrechnungstarifs:', response);
          alert('Fehler beim Kopieren des Verrechnungstarifs: ' + (response?.error || response));
        }
      });
    },
    applySubtarifTemplate() {
      if (!this.subtarifTemplateKey) return;
      const [type, id] = this.subtarifTemplateKey.split('-');
      const list = type === 'vt' ? this.verrechnungstarife : this.subtarife;
      const source = list.find(tarif => tarif.id === Number(id));
      if (!source) return;
      this.newSubtarif = {
        name: source.name,
        laufzeitVon: source.laufzeitVon,
        laufzeitBis: source.laufzeitBis,
        categoryPrices: { ...(source.categoryPrices || {}) }
      };
    },
    createSubtarif() {
      if (!this.isSubtarifValid) return;
      CASP.createSubtarif(this.newSubtarif, (status, response) => {
        if (status === 200) {
          this.newSubtarif = {
            name: '',
            laufzeitVon: '',
            laufzeitBis: '',
            categoryPrices: {}
          };
          this.subtarifTemplateKey = '';
          this.loadData();
        } else {
          alert('Fehler beim Erstellen des Sub-Tarifs: ' + (response?.error || response));
        }
      });
    },
    startEditSubtarif(tarif) {
      this.editingSubtarif = tarif.id;
      this.editSubtarifData = {
        name: tarif.name,
        laufzeitVon: tarif.laufzeitVon,
        laufzeitBis: tarif.laufzeitBis,
        categoryPrices: tarif.categoryPrices ? { ...tarif.categoryPrices } : {}
      };
    },
    saveSubtarif(id) {
      CASP.updateSubtarif(id, this.editSubtarifData, (status, response) => {
        if (status === 200) {
          this.editingSubtarif = null;
          this.loadData();
        } else {
          alert('Fehler beim Speichern des Sub-Tarifs: ' + (response?.error || response));
        }
      });
    },
    cancelEditSubtarif() {
      this.editingSubtarif = null;
      this.editSubtarifData = {
        name: '',
        laufzeitVon: '',
        laufzeitBis: '',
        categoryPrices: {}
      };
    },
    deleteSubtarif(id) {
      if (!confirm('Möchten Sie diesen Sub-Tarif wirklich löschen?')) return;
      CASP.deleteSubtarif(id, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          alert('Fehler beim Löschen des Sub-Tarifs: ' + (response?.error || response));
        }
      });
    },
    copySubtarif(tarif) {
      const laufzeitVon = prompt('Laufzeit Von (YYYY-MM-DD):', tarif.laufzeitVon);
      if (!laufzeitVon) return;
      const laufzeitBis = prompt('Laufzeit Bis (YYYY-MM-DD):', tarif.laufzeitBis);
      if (!laufzeitBis) return;
      CASP.copySubtarif(tarif.id, { laufzeitVon, laufzeitBis }, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          alert('Fehler beim Kopieren des Sub-Tarifs: ' + (response?.error || response));
        }
      });
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE');
    }
  },
  created() {
    document.getElementById("mainspinner").style.display = "none";
    if (localStorage) localStorage.setItem("last", "/admin/tarife");
    this.loadData();
  }
};
</script>

<style scoped>
.hyphens-auto {
  hyphens: auto;
}
</style>
